
import {
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  reactive,
  computed
} from 'vue'
import { useReflectiveInjector } from '@tanbo/vue-di-plugin'
import { UserService, UserInfo } from '@/services/user.service'
import { useRoute } from 'vue-router'
import { PayService } from './pay.service'
import { isDevOrTest } from '@/common/env'
import { scrollOpen, scrollClose, isVipForever } from '@/common/utils'

import PaySuccess from './_components/pay_success.vue'
import TipDialog from './_components/tip_dialog.vue'
import AcceptProtocol from './AcceptProtocol.vue'
import { ConfigService } from '@/services/config.service'

// 套餐渠道
const chan = isDevOrTest ? 'wechat_test' : 'wechat'
// 剩余名额pose_code
const pose_code_count = 'wx_permanent_limit'
// 用户权益
const privliegeList = [
  {
    name: '精品ppt',
    value: 'ppt'
  },
  {
    name: '文档简历',
    value: 'word'
  },
  {
    name: 'Excel报表',
    value: 'excel'
  },
  {
    name: '广告设计',
    value: 'ad'
  },
  {
    name: '专属客服',
    value: 'customer'
  },
  {
    name: 'PDF转Word',
    value: 'pdf2word'
  },
  {
    name: 'Word转PDF',
    value: 'word2pdf'
  },
  {
    name: '更多格式转换',
    value: 'convert_more'
  }
]

export default defineComponent({
  components: {
    PaySuccess, TipDialog, AcceptProtocol
  },
  setup() {
    const injector = useReflectiveInjector([PayService, ConfigService])
    const payService = injector.get(PayService)
    const configService = injector.get(ConfigService)
    const userService = injector.get(UserService)

    // 用户信息处理
    // TODO: imgAvatarRef放在userHandle无法进行ref绑定，有空需要处理下，放在userHandle中
    const imgAvatarRef = ref(null)
    const userHandle = reactive({
      userInfo: reactive<Partial<UserInfo>>({}),
      userInfoSub: null,
      subscribe: () => {
        userHandle.userInfoSub = userService.userInfo.subscribe((info) => {
          Object.assign(userHandle.userInfo, info)
        })
      },
      unsubscribe: () => {
        userHandle.userInfoSub.unsubscribe()
      },
      imgAvatarError: () => {
        const img = require('@/assets/images/icon_basic_avatar.png')
        imgAvatarRef.value.src = img
      }
    })
    userHandle.subscribe()

    // 权益列表
    const privliegeHandle = reactive({
      // 把数组根据count，分割为二维数组
      listSliceByCount: (list, count) => {
        const r = []
        for (let i = 0; i < list.length; i += count) {
          r.push(privliegeList.slice(i, i + count))
        }
        return r
      },
      privliegeListLayout: [],
      // 方便布局的权益列表
      createListLayout: () => {
        privliegeHandle.privliegeListLayout = privliegeHandle.listSliceByCount(privliegeList, 4)
      }
    })
    privliegeHandle.createListLayout()

    // 套餐剩余名额
    const leftCountHandle = {
      count: ref(0),
      load: () => {
        payService.getPackageCount({ pos_code: pose_code_count }).then((count) => {
          leftCountHandle.count.value = count
        })
      },
      update: () => {
        return payService.usePackage({ pos_code: pose_code_count })
      },
      updateAndReload: () => {
        leftCountHandle.update().then(() => {
          leftCountHandle.load()
        })
      }
    }

    // 上报
    const reportHandle = {
      source: useRoute().query.source || '',
      // 当前页面展示上报
      reportShow: () => {
        payService.reportPay({
          actiontype: 'show',
          uid: userHandle.userInfo.uid,
          source: reportHandle.source,
          order_id: '',
          order_num: '',
          ext: ''
        })
      },
      // 支付成功上报
      // orderId: 订单id，fee: 支付金额，单位分
      reportPayResult: (orderId, fee) => {
        payService.reportPay({
          actiontype: 'pay',
          uid: userHandle.userInfo.uid,
          source: reportHandle.source,
          order_id: orderId,
          order_num: fee,
          ext: ''
        })
      }
    }

    // 支付成功弹窗
    const paySuccessHandle = reactive({
      isShow: false,
      show: () => {
        paySuccessHandle.isShow = true
        scrollClose()
      },
      close: () => {
        paySuccessHandle.isShow = false
        scrollOpen()
      }
    })

    // 协议勾选
    const protocolHandle = reactive({
      isAgree: false,
      isShowAcceptModal: false,
      toggle: () => {
        protocolHandle.isAgree = !protocolHandle.isAgree
      },
      openUserProtocol: () => {
        window.open(configService.getUserProtocolUrl, '_blank')
      },
      openPrivacyPolicy: () => {
        window.open(configService.getPrivacyPolicyUrl, '_blank')
      }
    })

    // 套餐相关
    const packageHandle = reactive({
      list: [],
      packageSelect: {
        id: 0,
        name: '',
        priceOriginal: 0,
        priceReal: 0,
        tipFooter: ''
      },
      packageForever: null,
      selectPackage: packageItem => {
        packageHandle.packageSelect = packageItem
      },
      isSelect: packageItem => {
        return packageItem === packageHandle.packageSelect
      },
      isSelectForever: computed(() => packageHandle.isPackageForever(packageHandle.packageSelect)),
      isPackageForever: packageItem => {
        return packageItem === packageHandle.packageForever
      },
      getProductid: packageItem => {
        const req = {
          biztype: 1,
          paytype: 1,
          id: packageItem.id,
          source: ''
        }
        return payService.getApiRechargePrepare(req).then(res => res.product_id)
      },
      palceWxPayOrder: productId => {
          const req = {
            payapp: 'keniuoffice',
            product_id: productId,
            openid: userHandle.userInfo.openid
          }
          return payService.palceWxPayOrder(req)
      },
      paySuccess: (orderId, fee) => {
        reportHandle.reportPayResult(orderId, fee)
        paySuccessHandle.show()
        // 支付成功更新用户权益
        userService.updatePriliage()
        // 如果是买终身会员，更新剩余套餐数量，剩余套餐数量只对终身会员有效
        if (packageHandle.isSelectForever) {
          leftCountHandle.updateAndReload()
        }
      },
      clickPay: async () => {
        if (!protocolHandle.isAgree) {
          protocolHandle.isShowAcceptModal = true
          return
        }
        const isVipF = isVipForever(userHandle.userInfo?.privliegeEndTime)
        if (isVipF) {
          tipDialog.visible = true
          return
        }

        // 获取套餐对应产品id
        const productId = await packageHandle.getProductid(packageHandle.packageSelect)
        // 微信下单
        const order = await packageHandle.palceWxPayOrder(productId)

        // 根据下单返回的微信配置调起微信支付
        const wxPayConfig = order.wx_resp
        const orderId = order.order_id
        const fee = order.fee
        const paramsShowPay = {
          appId: wxPayConfig.appid,
          timeStamp: wxPayConfig.timestamp,
          nonceStr: wxPayConfig.noncestr,
          package: wxPayConfig.package,
          signType: wxPayConfig.sign_type,
          paySign: wxPayConfig.sign
        }
        payService.showWxPay(paramsShowPay, () => {
          packageHandle.paySuccess(orderId, fee)
        })
      },
      load: () => {
        const req = { chan: chan }
        return payService.getPackages(req).then((l) => {
          // 第一个套餐是永久套餐
          packageHandle.packageForever = l[0]
          // 自动选择第一个套餐
          packageHandle.packageSelect = l[0]
          packageHandle.list = l
        })
      }
    })

    const tipDialog = reactive({
      visible: false,
      title: '温馨提示',
      desc: '您已是终身会员，不需要重复支付，您可以前往PC端产品下载模板',
      emitClickSelfBtn () {
        tipDialog.visible = false
      },
      emitClickClose () {
        tipDialog.visible = false
      }
    })

    const onAcceptModalConfirm = () => {
      protocolHandle.isAgree = true
      protocolHandle.isShowAcceptModal = false
      packageHandle.clickPay()
    }

    const onAcceptModalCancel = () => {
      protocolHandle.isShowAcceptModal = false
    }

    // 永久会员tip
    const tipHanle = reactive({
      tipForeverVip: '',
      loadTipForeverVip: () => {
        return payService.getTipForever().then(tip => {
          tipHanle.tipForeverVip = tip
        })
      }
    })

    // 倒计时
    const countdownHandle = reactive({
      countdown: 0,
      timeout: null,
      timerInterval: 123,
      isShow: false,
      countdownObj: {
        h: '',
        m: '',
        s: '',
        ms: ''
      },
      updateCountdownObj: () => {
        const countdown = countdownHandle.countdown
        const countdownObj = countdownHandle.countdownObj
        // 根据时间戳计算各个时间数
        const hours = parseInt(String(countdown / (1000 * 60 * 60)))
        const minutes = parseInt(String((countdown % (1000 * 60 * 60)) / (1000 * 60)))
        const seconds = parseInt(String((countdown % (1000 * 60)) / 1000))
        const milliseconds = parseInt(String((countdown % (1000 * 60)) % 1000))
        // 时
        const strHours = hours < 10 ? ('0' + hours) : String(hours)
        countdownObj.h = strHours
        // 分
        const strMinutes = minutes < 10 ? ('0' + minutes) : String(minutes)
        countdownObj.m = strMinutes
        // 秒
        const strSeconds = seconds < 10 ? ('0' + seconds) : String(seconds)
        countdownObj.s = strSeconds
        // 毫秒
        if (milliseconds < 10) {
          countdownObj.ms = '00' + milliseconds
        } else if (milliseconds < 100) {
          countdownObj.ms = '0' + milliseconds
        } else {
          countdownObj.ms = String(milliseconds)
        }
      },
      updateCountdown: () => {
        // 更新countdown
        countdownHandle.countdown -= countdownHandle.timerInterval
        // 如果countdown小于0，即到了第二天，更新countdown为一天总时间加上countdown
        if (countdownHandle.countdown < 0) {
          const timestampOneDay = 24 * 60 * 60 * 1000
          countdownHandle.countdown = timestampOneDay + countdownHandle.countdown
        }
        // timeout循环计时
        countdownHandle.updateCountdownObj()
        countdownHandle.timeout = setTimeout(() => {
          countdownHandle.updateCountdown()
        }, countdownHandle.timerInterval)
      },
      // 倒计时展示支持后台控制
      loadIsShow: () => {
        payService.getIsShowInterval().then(isOpen => {
          if (isOpen) {
            countdownHandle.isShow = true
            countdownHandle.initTimeout()
          }
        })
      },
      initTimeout: () => {
        // 获取当前时间到今晚00:00的时间差
        const now = new Date().getTime()
        const todayEnd = new Date(now).setHours(23, 59, 59, 999)
        countdownHandle.countdown = todayEnd - now
        countdownHandle.updateCountdownObj()
        // 声明下次的计时
        countdownHandle.timeout = setTimeout(() => {
          countdownHandle.updateCountdown()
        }, countdownHandle.timerInterval)
      },
      clearTimeout: () => {
        clearTimeout(countdownHandle.timeout)
      }
    })

    onMounted(() => {
      // 加载套餐
      packageHandle.load()
      // 加载套餐剩余数
      leftCountHandle.load()
      // 加载永久vip tip
      tipHanle.loadTipForeverVip()
      // 加载倒计时是否展示
      countdownHandle.loadIsShow()
      // 页面展示上报
      reportHandle.reportShow()
    })

    onUnmounted(() => {
      userHandle.unsubscribe()
      countdownHandle.clearTimeout()
    })

    return {
      // 用户信息
      userHandle,
      imgAvatarRef,
      // 套餐列表
      packageHandle,
      // 倒计时
      countdownHandle,
      // tip
      tipHanle,
      // 支付成功弹窗
      paySuccessHandle,
      // 权益列表
      privliegeHandle,
      // 剩余名额
      leftCountHandle,
      /** 终身会员支付弹的tip */
      tipDialog,
      // 协议
      protocolHandle,
      onAcceptModalConfirm,
      onAcceptModalCancel
    }
  }
})
